import React from "react";
import { Controller, Control, FieldErrors } from "react-hook-form";
import Input from "../../components/shared/input";
import ThemedPhoneInput from "../../components/shared/themed-phone-input";
import ThemedCheckbox from "./ThemedCheckbox";
import { HIPAA_DEF, FieldDef } from "../config/hipaa";

type Props = {
  name: string;
  control: Control<Record<string, any>>;
  errors: FieldErrors<Record<string, any>>;
  formValues: Record<string, any>;
};

export function FieldRenderer({ name, control, errors, formValues }: Props) {
  const def = HIPAA_DEF.fields.find((f) => f.name === name) as FieldDef | undefined;
  if (!def) return null;

  const id = `rr-${def.name}`;
  const isRequired = ("required" in (def as any) && (def as any).required === true);
  const dynamicRequired = (
    (def.name === "purpose_other_text" && formValues.purpose_other) ||
    ((def.name === "date_from" || def.name === "date_to") && !formValues.dates_all) ||
    (def.name === "sensitive_initials" && (
      formValues.sensitive_mental_health ||
      formValues.sensitive_hiv_aids ||
      formValues.sensitive_genetic ||
      formValues.sensitive_substance_use
    )) ||
    (def.name === "representative_relationship" && formValues.representative_is_guardian)
  );

  const rules: any = {};
  if (isRequired || dynamicRequired) rules.required = "This field is required";
  if (def.type === "email") rules.pattern = { value: /.+@.+\..+/, message: "Enter a valid email" };

  const themedInputClass = "text-brown-900 placeholder:text-brown-400 border-terracotta-200 hover:border-terracotta-300 focus:border-terracotta-500";
  const labelClass = "text-terracotta-700";
  const helpClass = "text-terracotta-600";

  if (def.type === "checkbox") {
    return (
      <Controller
        key={def.name}
        control={control}
        name={def.name}
        rules={rules}
        render={({ field }) => (
          <div>
            <ThemedCheckbox id={id} label={def.label} checked={!!field.value} onChange={field.onChange} />
            {(errors as any)[def.name] && (
              <span className="text-red-500 mt-1 block text-xs">
                {(errors as any)[def.name]?.message as string}
              </span>
            )}
          </div>
        )}
      />
    );
  }

  if (def.name === "patient_phone") {
    return (
      <Controller
        key={def.name}
        control={control}
        name={def.name}
        rules={rules}
        render={({ field }) => (
          <ThemedPhoneInput
            id={id}
            label={def.label}
            value={field.value}
            onChange={field.onChange}
            className={themedInputClass}
            labelClassName={labelClass}
            helperTextClassName={helpClass}
            error={(errors as any)[def.name]?.message as string}
            fullWidth
          />
        )}
      />
    );
  }

  const helperText: string | undefined =
    def.name === "expiration_date" ? "Default is one year from today." : undefined;

  return (
    <Controller
      key={def.name}
      control={control}
      name={def.name}
      rules={rules}
      render={({ field }) => (
        <Input
          id={id}
          type={def.type === "initials" ? "text" : (def.type as any)}
          label={`${def.label}${isRequired || dynamicRequired ? " *" : ""}`}
          placeholder={def.type === "date" ? "YYYY-MM-DD" : def.label}
          value={def.type === "initials" ? String(field.value || "").toUpperCase().slice(0, 3) : field.value || ""}
          onChange={(e) => field.onChange(def.type === "initials" ? e.target.value.toUpperCase().slice(0, 3) : e.target.value)}
          className={themedInputClass}
          labelClassName={labelClass}
          helperTextClassName={helpClass}
          helperText={helperText}
          error={(errors as any)[def.name]?.message as string}
          fullWidth
        />
      )}
    />
  );
}

export default FieldRenderer;

